import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ASSET_KEY, assetSource } from 'shared/utils/assetSource';
import AnimationWrapper from 'shared/components/Animations/AnimationWrapper';
import { ANIMATION_KEYS } from 'shared/components/Animations/constants';
import styled from 'styled-components';

const PiggyBankCoinsWeb = forwardRef(({ coinSizeDvw = 9 }, ref) => {
  const [coins, setCoins] = useState([]);
  const cleanupTimer = useRef(null);

  const handlePress = () => {
    const numCoins = 30;
    const coinsArray = [];

    for (let i = 0; i < numCoins; i++) {
      coinsArray.push({
        id: i,
        startXPx: window.innerWidth / 2,
        startYPx: window.innerHeight / 2,
        offsetX: Math.random() * 250 - 125,
        offsetY: 100 + i * 5,
        duration: 1000 + Math.random() * 400,
        delay: i * 50,
      });
    }

    setCoins(coinsArray);
    clearTimeout(cleanupTimer.current);
    cleanupTimer.current = setTimeout(() => setCoins([]), 4500);
  };

  useImperativeHandle(ref, () => ({
    triggerAnimation: handlePress,
  }));

  useEffect(() => () => clearTimeout(cleanupTimer.current), []);

  return (
    <AnimationWrapper animationKey={ANIMATION_KEYS.PIGGY_BANK_COINS}>
      <Container>
        {coins.map(coin => (
          <AnimatedCoin
            key={coin.id}
            coin={coin}
            coinSrc={assetSource(ASSET_KEY.COIN_GLOW)}
            coinSizeDvw={coinSizeDvw}
          />
        ))}
      </Container>
    </AnimationWrapper>
  );
});

PiggyBankCoinsWeb.displayName = 'PiggyBankCoinsWeb';

function AnimatedCoin({ coin, coinSrc, coinSizeDvw }) {
  const { startXPx, startYPx, offsetX, offsetY, duration, delay } = coin;

  const coinSizePx = (window.innerWidth * coinSizeDvw) / 100;
  const half = coinSizePx / 2;

  return (
    <motion.img
      src={typeof coinSrc === 'string' ? coinSrc : coinSrc?.uri || ''}
      alt=""
      style={{
        position: 'absolute',
        width: `${coinSizeDvw}dvw`,
        height: `${coinSizeDvw}dvw`,
        left: startXPx,
        top: startYPx,
        willChange: 'transform, opacity',
      }}
      initial={{ opacity: 0, x: -half, y: -half }}
      animate={{
        x: [-half, -half + offsetX * 0.6, -half + offsetX],
        y: [
          -half,                        // start Y
          -half - offsetY,              // go up
          -half + offsetY * 2 + 300,    // fall
        ],
        opacity: [0, 1, 1, 0],
        rotate: [0, Math.random() * 360, Math.random() * 720], // rotate coins
      }}
      transition={{
        delay: delay / 1000,
        duration: duration / 800,
        times: [0, 0.4, 1],
        ease: ['easeOut', 'easeInOut'],
        opacity: {
          duration: duration / 1000,
          times: [0, 0.2, 0.8, 1],
          ease: 'linear',
        },
      }}
    />
  );
}

export default PiggyBankCoinsWeb;

const Container = styled.div`
  position: fixed;
  inset: 0;
  width: 100dvw;
  height: 100dvh;
  pointer-events: none;
  z-index: 9999;
`;
